/**
 * Pure state mapping for the top-bar chat design (2026-08-14
 * new-chatbox-topbar). Turns the chat-status spine (`useChatStatuses`) +
 * the active-chat rows (`useActiveChats`) into the design's own five-state
 * vocabulary — no React, no fetching, fully unit-testable.
 *
 * Design source: `Eddy Design Files/New Chatbox/source/eddy-chatbar.jsx`'s
 * `CHAT_STATE`/`stateOf`/`countStates`. The prototype only knows four states
 * (running/needs-you/ready/stalled) because a finished-and-seen chat simply
 * disappears from its seed data; our real data has no such disappearing act,
 * so a `done-seen` (or status-less) chat maps to a fifth, quiet `earlier`
 * state — the board's own "Earlier" tail group, and the ONLY
 * state left out of the cluster's dots.
 *
 * 2026-08-15: `ready` (finished, not opened yet) used to be left out of those
 * dots too, which meant the cluster read "All quiet" while green unread
 * answers sat waiting — visible only if you opened the board. Quiet now means
 * "nothing is left for you", not "nothing is happening", so `ready` counts.
 */
import type { ChatStatus, ChatStatusState } from '~/hooks/queries/use-chat-status';
import type { ChatThreadRow } from '~/hooks/queries/use-chats';
import type { CodingSessionView } from '~/features/coding/coding-events';
import type { ProBuilderSessionView } from '~/hooks/queries/use-pro-builder-session';
import { formatRelativeTime } from '~/lib/relative-time';
import { formatStamp } from '~/lib/local-datetime';
import { PRO_BUILDER_NEEDS_OK } from '~/features/pro-builder/pro-builder-copy';

export type BarChatState = 'asking' | 'running' | 'ready' | 'stalled' | 'earlier';

/**
 * FRONT-1681: which build-outcome copy a build row's `earlier` status line
 * needs, and the relative time to append to it — computed at RENDER time by
 * `statusLineFor`, never baked in at map time. TanStack's structural sharing
 * returns the identical `sessions` array reference when a poll response is
 * deep-equal, so on a quiet account `mapCodingToBar`'s memo in
 * `use-chat-activity.ts` never recomputes — a time string baked in at map
 * time would freeze right there while the sibling chat rows (which format at
 * render time in `statusLineFor`'s own 'earlier' branch) keep ticking.
 */
export type BuildTimeReason = 'finished' | 'stopped' | 'failed';

export interface BarChat {
  sessionId: string;
  title: string;
  state: BarChatState;
  lastActiveAt: string | null;
  /** FRONT-1681: a chat row or a Building Session row — the board renders
   *  and routes the two differently (build icon + "Build" tag, opens the
   *  coding window rather than a chat). Required, not defaulted, so a new
   *  producer of `BarChat` is forced to say which it is rather than
   *  silently landing on 'chat'. */
  kind: 'chat' | 'build' | 'pro-builder';
  /** FRONT-2522: the agent a chat is pinned to (direct agent chat). Set only
   *  by the chat mapper, from `ChatThreadRow.directAgent`; absent otherwise. */
  directAgent?: { id: string; name: string };
  /** Only set for `stalled` — the failure cause off the status spine, when
   *  the server supplied one. */
  cause?: string;
  /** Only set for `asking` — what the chat waits on (FRONT-2603). */
  waitingOn?: ChatStatus['waitingOn'];
  /**
   * FRONT-1681: a precomputed, fully STATIC status-line override — set by
   * `mapCodingToBar` (a build row's copy doesn't come from the chat-status
   * spine at all). Chat rows never set this, so `statusLineFor`'s existing
   * per-state switch keeps deciding for them, unchanged. Never used for a
   * line that carries a relative time — see `buildTimeReason`.
   */
  statusLine?: string;
  /** FRONT-1681: set instead of `statusLine` when the build row's `earlier`
   *  copy needs a relative time computed at render time — see
   *  `BuildTimeReason`'s doc. Undefined for a chat row and for any build row
   *  whose line is fully static. */
  buildTimeReason?: BuildTimeReason;
}

export interface BarCounts {
  running: number;
  asking: number;
  stalled: number;
  /** Finished, not opened yet — the green "unread" count. Part of the ambient
   *  cluster since 2026-08-15: a landed answer nobody has read is exactly the
   *  thing the readout exists to surface. */
  ready: number;
}

const STATUS_TO_BAR: Record<ChatStatusState, BarChatState> = {
  working: 'running',
  'needs-you': 'asking',
  failed: 'stalled',
  'done-unseen': 'ready',
  'done-seen': 'earlier',
};

/**
 * FRONT-2433: one chat's bar state from its status-spine entry — the same
 * mapping the top bar uses, exported so the Chats page's row dots speak the
 * identical four-state vocabulary. No entry → the quiet `earlier` state.
 */
export function barStateForStatus(status: ChatStatus | undefined): BarChatState {
  return status ? STATUS_TO_BAR[status.state] : 'earlier';
}

/**
 * Maps each active chat row onto the bar's state. A chat with no entry in
 * the status map at all (never run, or aged out of the spine) is treated the
 * same as `done-seen` — the quiet `earlier` tail — never dropped.
 */
export function mapChatsToBar(
  chats: ChatThreadRow[],
  statuses: Record<string, ChatStatus>,
): BarChat[] {
  return chats.map((chat) => {
    const status = statuses[chat.sessionId];
    const state: BarChatState = barStateForStatus(status);
    return {
      sessionId: chat.sessionId,
      title: chat.title ?? 'New chat',
      state,
      kind: 'chat',
      lastActiveAt: chat.lastActiveAt,
      cause: state === 'stalled' ? status?.cause : undefined,
      ...(state === 'asking' && status?.waitingOn ? { waitingOn: status.waitingOn } : {}),
      ...(chat.directAgent ? { directAgent: chat.directAgent } : {}),
    };
  });
}

/** How far back a Building Session still shows in the chat overview at all —
 *  a build backlog can run long, and the board is meant to surface what's
 *  live, not double as the coding window's own full project switcher. */
const BUILD_ROW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** How many of the (already 7-day-windowed) most-recently-updated builds the
 *  board shows at once. */
const BUILD_ROW_CAP = 10;

interface ClassifiedBuild {
  session: CodingSessionView;
  state: BarChatState;
  statusLine?: string;
  buildTimeReason?: BuildTimeReason;
}

/**
 * One Building Session → its bar state + status-line inputs. Split out of
 * `mapCodingToBar` so the cap-ordering sort below can rank by state BEFORE
 * any row is dropped — see that function's doc.
 *
 * Build-lifecycle round: a build now follows the SAME five-state lifecycle
 * a chat does, including the green→gray "seen" step — Mattias's own words,
 * "It is no different than a chat for the user". `session.seen` (server-
 * computed, `src/coding/seen.ts`, off the same `chat_seen` marker the chat
 * spine uses) replaces the old 24h failure-staleness heuristic wholesale: a
 * failed OR finished build now stays in its urgent colour (`stalled` /
 * `ready`) for as long as it takes the user to actually open it, exactly
 * like a chat — never on a timer.
 */
function classifyBuild(session: CodingSessionView): ClassifiedBuild {
  // 1. A session parked on a pending ask_user question is ALSO
  //    `status === 'running'` on the row itself (see the backend's
  //    getCodingSessionsAwaitingAnswer doc comment), so this check must run
  //    before every status check below, or an awaiting build would never
  //    show as asking.
  if (session.awaitingAnswer) {
    return { session, state: 'asking', statusLine: 'Waiting on your answer' };
  }
  if (session.status === 'running') {
    // 2. `status === 'running'` alone is not enough — `runLive` is the
    //    authoritative liveness signal (src/coding/routes.ts,
    //    computeRunLive): the in-memory per-replica claim it partly rests
    //    on can only ever be ADDED, never retired for a run whose process
    //    died, so a row can sit at status=running with a long-expired lease
    //    forever. Without this check that row renders a permanent blue
    //    "running" dot and keeps the whole cluster's "all quiet" state
    //    unreachable.
    if (session.runLive) {
      return { session, state: 'running', statusLine: 'Building…' };
    }
    return { session, state: 'earlier', buildTimeReason: 'stopped' };
  }
  if (session.status === 'failed') {
    // 3. An UNSEEN failure holds the chrome's red dot; once opened it
    //    demotes to the quiet `earlier` tail, same relative-time copy as
    //    before — just gated on `seen` now instead of a 24h clock.
    if (!session.seen) {
      return { session, state: 'stalled', statusLine: session.lastError || 'Build failed' };
    }
    return { session, state: 'earlier', buildTimeReason: 'failed' };
  }
  // 4. idle
  // FRONT-1681 (review round): "has this build ever run" is
  // `lastRunEndedAt != null` — NOT `sdkSessionId`. `sdkSessionId` is a
  // resume HANDLE, not run history: `maybeAutoClear` (auto-clear on a
  // context-heavy finish) and "Clear & continue" both null it out on a
  // session that has definitely run, which used to send an auto-cleared
  // build straight back to "Not started yet" and it could never turn
  // green again — Mattias's reported bug, coming back through another
  // door. `lastRunEndedAt` is stamped only when a run actually ends
  // (`src/coding/store.ts`), so it survives both of those clears.
  if (session.lastRunEndedAt != null) {
    // FRONT-1681 (third review round): a deliberately-Stopped run also
    // lands here (status idle, a fresh lastRunEndedAt) — it must not claim
    // "Build finished" alongside a genuine success. `lastRunOutcome` is the
    // one signal that tells the two apart; `null` (a never-backfilled
    // legacy row, or the never/non-terminal-tape cases the backfill leaves
    // untouched) degrades to the pre-existing "finished" wording rather
    // than guessing "stopped".
    const reason: BuildTimeReason = session.lastRunOutcome === 'interrupted' ? 'stopped' : 'finished';
    // An UNSEEN finished/stopped build is a result waiting to be read — the
    // green `ready` group, same copy either way; `seen` only decides the
    // colour, never which of the two labels above applies.
    return session.seen
      ? { session, state: 'earlier', buildTimeReason: reason }
      : { session, state: 'ready', buildTimeReason: reason };
  }
  return { session, state: 'earlier', statusLine: 'Not started yet' };
}

/**
 * Maps Building Sessions (coding sessions, ADR-0028) onto the bar's
 * five-state vocabulary (FRONT-1681). Precedence — see `classifyBuild`
 * above for the full per-state breakdown (awaitingAnswer > running+live >
 * failed+unseen > idle+finished+unseen > everything else, `earlier`).
 *
 * Scope: only sessions updated in the last 7 days. Capped at the 10 most
 * recently touched, but the cap is applied AFTER ranking every in-window
 * row's state ahead of recency — `asking`/`running`/`ready`/`stalled` (every
 * state but the quiet `earlier` tail) always sort before `earlier`,
 * regardless of timestamp. Without that ordering, ten recently-touched idle
 * builds can crowd an older build that's genuinely waiting on the user's
 * answer (or an unread finished one) out of the cap entirely — and unlike a
 * chat, there is no separate "read it once and it clears" affordance to
 * notice it went missing; its amber count would simply vanish from the
 * readout.
 *
 * FRONT-1681 (review round): the rank is `GROUP_ORDER.indexOf(state)`, the
 * board's own fixed display order — NOT a binary actionable-vs-not flag.
 * A binary flag makes every actionable state equally rank-0, so once
 * `ready` joined the actionable set, ten recent unread-finished (`ready`)
 * builds could still push an older `asking` build past the cap — the exact
 * silent loss this ordering exists to prevent, just moved one state over.
 * Ranking by the full fixed order keeps `asking` ahead of `ready` ahead of
 * `stalled` ahead of `earlier` no matter how many rows crowd any one state.
 */
export function mapCodingToBar(sessions: CodingSessionView[], now: Date = new Date()): BarChat[] {
  const cutoffMs = now.getTime() - BUILD_ROW_WINDOW_MS;
  const inWindow = sessions.filter((s) => {
    const updatedMs = Date.parse(s.updatedAt);
    return Number.isFinite(updatedMs) && updatedMs >= cutoffMs;
  });
  const classified = inWindow.map((session) => classifyBuild(session));
  classified.sort((a, b) => {
    const aRank = GROUP_ORDER.indexOf(a.state);
    const bRank = GROUP_ORDER.indexOf(b.state);
    if (aRank !== bRank) return aRank - bRank;
    return Date.parse(b.session.updatedAt) - Date.parse(a.session.updatedAt);
  });
  return classified.slice(0, BUILD_ROW_CAP).map(({ session, state, statusLine, buildTimeReason }) => ({
    sessionId: session.id,
    title: session.title,
    state,
    kind: 'build',
    lastActiveAt: session.updatedAt,
    statusLine,
    buildTimeReason,
  }));
}

/**
 * FRONT-3040: one Pro Builder session (the person's own Claude Code) → its bar
 * state and static status line. Claude Code's own hooks report `state`; the
 * server adds `live` (the PTY is still alive) and `seen` (nothing unseen).
 * A state that needs the process (working, needs-you) means nothing once the
 * process is gone, so it falls to `earlier` with "Session ended"; the two
 * outcomes (failed, finished) keep their urgent colour until the person has
 * looked (`seen`), whether or not the shell is still alive. A consent card
 * waiting on a live terminal (`consentWaiting`) is `asking` / "Needs your OK"
 * whatever `state` says; on an ended terminal the flag is ignored. `running` carries
 * no status line of its own (the board shows the "$ building" tag instead).
 */
export function classifyProBuilder(
  view: Pick<ProBuilderSessionView, 'state' | 'live' | 'seen'> & Partial<Pick<ProBuilderSessionView, 'consentWaiting'>>,
): { state: BarChatState; statusLine?: string } {
  const { state, live, seen } = view;
  // FRONT-3108: a consent card waiting on a LIVE terminal outranks whatever
  // Claude Code's hooks last said (it is `working`, blocked on the card).
  if (live && view.consentWaiting === true) return { state: 'asking', statusLine: PRO_BUILDER_NEEDS_OK };
  switch (state) {
    case 'working':
      return live ? { state: 'running' } : { state: 'earlier', statusLine: 'Session ended' };
    case 'needs-you':
      return live
        ? { state: 'asking', statusLine: 'Waiting on you' }
        : { state: 'earlier', statusLine: 'Session ended' };
    case 'failed':
      return { state: seen ? 'earlier' : 'stalled', statusLine: 'Stopped on an error' };
    case 'finished':
      return { state: seen ? 'earlier' : 'ready', statusLine: 'Claude Code finished' };
    case 'idle':
      return { state: 'earlier', statusLine: 'Idle' };
    case 'ended':
      return { state: 'earlier', statusLine: 'Session ended' };
    case null:
      return { state: 'earlier', statusLine: live ? 'Terminal open' : 'Session ended' };
  }
}

/**
 * FRONT-3040: the Pro Builder session as 0 or 1 board row. Dropped when it has
 * no usable last-activity time or it is older than the same 7-day window
 * builds use (the server drops those too; this is the client's own clock).
 */
export function mapProBuilderToBar(view: ProBuilderSessionView | null, now: Date = new Date()): BarChat[] {
  if (!view) return [];
  const lastActiveAt = view.stateAt ?? view.startedAt;
  const lastMs = Date.parse(lastActiveAt);
  if (!Number.isFinite(lastMs) || lastMs < now.getTime() - BUILD_ROW_WINDOW_MS) return [];
  const { state, statusLine } = classifyProBuilder(view);
  return [
    {
      sessionId: `pro-builder:${view.id}`,
      title: 'Pro Builder',
      state,
      kind: 'pro-builder',
      lastActiveAt,
      ...(statusLine !== undefined ? { statusLine } : {}),
    },
  ];
}

/** Fixed board group order — asking you first, the quiet tail last. */
/** The four states that want you, in the board's order — its groups, its
 *  count pills, and the Chats page's groups all read this one list.
 *  FRONT-2433: `ready` ("Done") sits above `running` — a finished result
 *  waiting for you outranks one still working on its own. */
export const ATTENTION_ORDER = ['asking', 'ready', 'running', 'stalled'] as const;

const GROUP_ORDER: readonly BarChatState[] = [...ATTENTION_ORDER, 'earlier'];

export interface BarGroup {
  state: BarChatState;
  items: BarChat[];
}

/** Groups bar chats for the activity board, in the fixed order above. Empty
 *  groups are omitted entirely — the board never shows a zero-count group. */
export function groupBarChats(chats: BarChat[]): BarGroup[] {
  return GROUP_ORDER.map((state) => ({
    state,
    items: chats.filter((c) => c.state === state),
  })).filter((g) => g.items.length > 0);
}

/** Cluster dot counts — running/asking/stalled/ready. Only `earlier` (read and
 *  grey) stays out of the ambient dots, because it is the one state that asks
 *  nothing of the user. */
export function countBarChats(chats: BarChat[]): BarCounts {
  const counts: BarCounts = { running: 0, asking: 0, stalled: 0, ready: 0 };
  for (const c of chats) {
    if (c.state !== 'earlier') counts[c.state] += 1;
  }
  return counts;
}

/**
 * Whether the cluster has anything ambient to show at all — drives the
 * "All quiet" fallback text. Quiet means "nothing is left for you", not
 * "nothing is happening": a finished-but-unopened chat keeps the bar awake
 * until it is read, which is the whole point of the green count.
 */
export function isAllQuiet(counts: BarCounts): boolean {
  return (
    counts.running === 0 && counts.asking === 0 && counts.stalled === 0 && counts.ready === 0
  );
}

/** The first chat waiting on the user, for the ask chip — `undefined` when
 *  none is asking (the chip then renders nothing). */
export function firstAsking(chats: BarChat[]): BarChat | undefined {
  return chats.find((c) => c.state === 'asking');
}

/**
 * The board row's accessible name (FRONT-1429) — the same fixed shape the
 * classic pill rail uses, so one locator finds a chat on either surface.
 * Named here rather than inline in `activity-board.tsx` so it can be pinned
 * by a fast unit test: `tests/e2e/` reopens chats by this exact name, and a
 * silent rename would otherwise only surface in a slow browser run.
 */
export function boardRowLabel(chat: BarChat): string {
  if (chat.kind === 'pro-builder') return 'Open Pro Builder';
  return chat.kind === 'build' ? `Open build: ${chat.title}` : `Open chat: ${chat.title}`;
}

/** Static half of a build's `earlier` copy per `BuildTimeReason` — the
 *  relative-time suffix is appended at render time by `statusLineFor`. */
const BUILD_TIME_LABEL: Record<BuildTimeReason, string> = {
  finished: 'Build finished',
  stopped: 'Build stopped',
  failed: 'Build failed',
};

/**
 * The board row's status line. A build row in the `earlier` state whose
 * copy needs a relative time carries `buildTimeReason` instead of a plain
 * `statusLine` — formatted HERE, at render time, off the row's own
 * `lastActiveAt`, never baked in at map time (see `BuildTimeReason`'s doc on
 * why: TanStack's structural sharing can freeze a baked-in time for many
 * polls in a row on a quiet account). `formatRelativeTime` only ever
 * returns null for a missing/invalid input; a build row's `lastActiveAt` is
 * always a real timestamp by construction, but the fallback stays reachable
 * for any other caller building a `BarChat` by hand (state-mapping.test.ts
 * exercises it directly).
 *
 * A build row with no `buildTimeReason` carries its own precomputed, fully
 * static `statusLine` (set by `mapCodingToBar` — a Building Session's copy
 * isn't derived from the chat-status spine at all); when that's present it
 * wins outright, so a chat row (which never sets either field) falls
 * through to the existing per-state switch below unchanged. The chat list
 * itself carries no message text (no `statusLine`/last-assistant-message
 * field exists on `ChatThreadRow`), so that switch is short state-based
 * copy — never an invented snippet — except for `stalled`, which surfaces
 * the real failure cause when the server supplied one.
 */
export function statusLineFor(chat: BarChat): string {
  if (chat.buildTimeReason) {
    const label = BUILD_TIME_LABEL[chat.buildTimeReason];
    const rel = formatRelativeTime(chat.lastActiveAt);
    return rel ? `${label} · ${rel}` : label;
  }
  if (chat.statusLine !== undefined) return chat.statusLine;
  switch (chat.state) {
    case 'stalled':
      return chat.cause || 'Something went wrong';
    case 'asking': {
      const line = waitingLineFor(chat);
      return line ? `${line.prefix} ${line.text}` : 'Waiting on your answer';
    }
    case 'running':
      return 'Working…';
    case 'ready':
      return 'Ready to review';
    case 'earlier': {
      const rel = formatRelativeTime(chat.lastActiveAt);
      return rel ? `Last active ${rel}` : 'No recent activity';
    }
  }
}

const WAITING_PREFIX: Record<NonNullable<BarChat['waitingOn']>['kind'], string> = {
  question: 'Asks:',
  approval: 'Needs your OK:',
  plan: 'Plan to approve:',
};

/** FRONT-2603: an asking row's lead-in and the model-written text after it,
 *  kept apart so the text can be rendered as inline Markdown. */
export function waitingLineFor(chat: BarChat): { prefix: string; text: string } | null {
  if (chat.state !== 'asking' || !chat.waitingOn) return null;
  return { prefix: WAITING_PREFIX[chat.waitingOn.kind], text: chat.waitingOn.text };
}

/** The board row's right-hand time label (design's `startedAt`) — a plain
 *  local HH:MM off `lastActiveAt`, or '' when there's nothing to show. */
export function formatBarTime(iso: string | null, now?: Date): string {
  // FRONT-2489: a bare clock on a five-day-old chat read as this morning.
  return formatStamp(iso, now);
}

/** FRONT-3108: the Pro Builder row that is asking because a consent card waits (see `classifyProBuilder`). */
function isProBuilderConsentRow(chat: Partial<Pick<BarChat, 'kind' | 'statusLine'>>): boolean {
  return chat.kind === 'pro-builder' && chat.statusLine === PRO_BUILDER_NEEDS_OK;
}

/** The board row / ask chip's hover action verb, per state. */
export function actionVerbFor(chat: Pick<BarChat, 'state'> & Partial<Pick<BarChat, 'kind' | 'statusLine'>>): string {
  if (chat.state === 'asking') return isProBuilderConsentRow(chat) ? 'Approve' : 'Answer';
  if (chat.state === 'stalled') return 'Fix';
  return 'Open';
}

/** Minimal shape this module needs from an approval row — kept narrow so
 *  callers don't have to import the full `Approval` type from the query
 *  hook just to call this pure helper. */
export interface AskApprovalLike {
  originExternalId?: string | null;
  description: string;
  /** Approval lifecycle status — resolved rows can arrive mid-refetch (SSE
   *  folds pushed rows straight into the cache), so consumers must be able
   *  to skip them; see `pending-approval-card.tsx`'s matching guard. */
  status?: string;
}

export interface AskChipInfo {
  sessionId: string;
  title: string;
  /** Approval description when a matching pending approval exists, else the
   *  chat's own title — the design's "spelled out, not just a dot" bet. */
  label: string;
  action: string;
  /** FRONT-1681: which surface the chip's click must open — a chat via
   *  `onOpenChat`, a Building Session via `onOpenBuild`. */
  kind: 'chat' | 'build' | 'pro-builder';
}

/**
 * The ask chip's content: prefer a matching still-pending approval's
 * description (matched by `originExternalId === chat.sessionId`) over the
 * chat title — the chat title is only ever the fallback (design README §3:
 * `askLine || title`). Already-resolved approvals are skipped: SSE pushes
 * settled rows into the same cache, and quoting one would put an "Approve"
 * verb on a chat that is actually blocked on something else. Returns `null`
 * when there's no asking chat at all, so the chip renders nothing.
 *
 * FRONT-1681: `firstAsking` can now return a Building Session row —
 * approvals are a chat-only mechanism (coding sessions never create one), so
 * the match is skipped entirely for a build (and for the Pro Builder row,
 * FRONT-3040, for the same reason). Without this, a build session
 * id could in principle coincide with some other chat's approval's
 * `originExternalId` and put an "Approve" verb — routed by the caller as
 * `onOpenChat` — on a chat that doesn't exist for this row.
 */
export function resolveAskChip(
  chat: BarChat | undefined,
  approvals: AskApprovalLike[],
): AskChipInfo | null {
  if (!chat) return null;
  const approval =
    chat.kind !== 'chat'
      ? undefined
      : approvals.find(
          (a) =>
            a.originExternalId === chat.sessionId &&
            a.status !== 'approved' &&
            a.status !== 'rejected',
        );
  return {
    sessionId: chat.sessionId,
    title: chat.title,
    label: approval ? approval.description : chat.title,
    action: approval || isProBuilderConsentRow(chat) ? 'Approve' : 'Answer',
    kind: chat.kind,
  };
}
